import { PropTypes } from 'typed/imports';
import { ComponentEventHandler1, PureComponent, ReactEventHandler2 } from 'typed/components/utils';
import { ComponentElement, ReactElement, ReactNode, isValidElement } from 'react';
import { default as FormattedResource, Resource, isResource, resourcePropType } from '../FormattedResource/FormattedResource';
import TextInput from 'components/common/TextInput/TextInput';
import Checkbox from '../Checkbox/Checkbox';
import { assertNever, toArray } from 'typed/utils';
import './Listbox.less';

export enum Appearance { LINES, SWITCHES }

export const valuePropType = PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]);

export interface LabelProps {
    className?: string;
    children?: ReactNode;
}

export class Label extends PureComponent<LabelProps> {
    static readonly displayName = 'Listbox.Label';

    static readonly propTypes = { className: PropTypes.string };

    static readonly defaultProps = { className: '' };

    render(): ReactElement {
        throw new Error('Listbox.Label can be used only inside Listbox');
    }
}

export interface GroupProps<TValue> {
    id: TValue;
    title: ReactElement | Resource;
    disabled?: boolean;
    className?: string;
    selectable?: boolean;
    children?: Children<TValue>;
}

export class Group<TValue> extends PureComponent<GroupProps<TValue>> {
    static readonly displayName = 'Listbox.Group';

    static readonly propTypes = {
        id: valuePropType.isRequired,
        title: PropTypes.oneOfType([resourcePropType, PropTypes.element]),
        disabled: PropTypes.bool,
        className: PropTypes.string,
        selectable: PropTypes.bool
    };

    static readonly defaultProps = {
        className: '',
        selectable: true
    };

    render(): ReactElement {
        throw new Error('Listbox.Group can be used only inside Listbox');
    }
}

export interface OptionProps<TValue> {
    value: TValue;
    keywords?: string;
    disabled?: boolean;
    className?: string;
    children?: ReactNode;
}

export class Option<TValue> extends PureComponent<OptionProps<TValue>> {
    static readonly displayName = 'Listbox.Option';

    static readonly propTypes = {
        value: valuePropType,
        keywords: PropTypes.string,
        disabled: PropTypes.bool,
        className: PropTypes.string
    };

    static readonly defaultProps = { className: '' };

    render(): ReactElement {
        throw new Error('Listbox.Option can be used only inside Listbox');
    }
}

export type Child<TValue> =
    | undefined | null
    | ComponentElement<LabelProps, Label>
    | ComponentElement<GroupProps<TValue>, Group<TValue>>
    | ComponentElement<OptionProps<TValue>, Option<TValue>>
    | ReactElement; // TODO validate children at compile time? for some reason `<Option />` has type of Element, with no information of it's origin

export type Children<TValue> = Child<TValue> | Child<TValue>[] | (Child<TValue> | Child<TValue>[])[];

const
    isImpossibleChild = (_child: ReactElement): _child is ReactElement => false,
    isLabel = (child: ReactElement): child is ReactElement<LabelProps> => !!child && typeof child === 'object' && child.type === Label,
    isGroup = <TValue extends any>(child: ReactElement): child is ReactElement<GroupProps<TValue>> => !!child && typeof child === 'object' && child.type === Group,
    isOption = <TValue extends any>(child: ReactElement): child is ReactElement<OptionProps<TValue>> => !!child && typeof child === 'object' && child.type === Option;

function keyify<TValue>(child: Child<TValue>, groupKey: string): { key: string; child: Child<TValue>; };
function keyify<TValue>(child: Child<TValue>[], groupKey: string): { key: string; child: Child<TValue>; }[];
function keyify<TValue>(child: Child<TValue> | Child<TValue>[], groupKey: string): { key: string; child: Child<TValue>; } | { key: string; child: Child<TValue>; }[] {
    return Array.isArray(child) ?
        child.map((subchild, subindex) => keyify(subchild, `${groupKey}.${subindex}`)) :
        {
            key: child?.key ? `external-${child.key}` : `index-${groupKey}`,
            child
        };
}

export interface Props<TValue> {
    value?: TValue | null;
    appearance?: Appearance;
    disabled?: boolean;
    searchable?: boolean;
    searchPlaceholder?: Resource;
    clearSearchFilterAutomatically?: boolean;
    tools?: ReactElement[];
    groupSelectionEnabled?: boolean; // TODO unify groups and options somehow, at least id and value props
    className?: string;
    children?: Children<TValue>;
    onChange?: ComponentEventHandler1<TValue>;
}

interface State<TValue> {
    groupExpansionStates: Map<TValue, true | false | undefined>;
    filter: string;
}

export default class Listbox<TValue extends number | string | boolean> extends PureComponent<Props<TValue>, State<TValue>> {
    static readonly Appearance = Appearance;
    static readonly valuePropType = valuePropType;
    static readonly Label = Label;
    static readonly Group = Group;
    static readonly Option = Option;

    static readonly propTypes = {
        value: valuePropType,
        appearance: PropTypes.oneOf(Object.values(Appearance)),
        disabled: PropTypes.bool,
        searchable: PropTypes.bool,
        searchPlaceholder: resourcePropType,
        clearSearchFilterAutomatically: PropTypes.bool,
        tools: PropTypes.arrayOf(PropTypes.element),
        groupSelectionEnabled: PropTypes.bool, // TODO unify groups and options somehow, at least id and value props
        className: PropTypes.string,
        onChange: PropTypes.func,
        onSearchChange: PropTypes.func, // TODO remove - search is internal feature
        autofocusOnSearch: PropTypes.bool // TODO remove - there might be multiple Listboxes on one page
    };

    static readonly defaultProps = {
        value: null,
        appearance: Appearance.LINES,
        disabled: false,
        searchable: false,
        searchPlaceholder: { id: 'common.search' },
        clearSearchFilterAutomatically: true,
        groupSelectionEnabled: false,
        className: '',
        autofocusOnSearch: false
    };

    static readonly contextTypes = { intl: PropTypes.object };

    state: State<TValue> = {
        groupExpansionStates: new Map<TValue, true | false | undefined>(),
        filter: ''
    };

    componentWillMount() {
        this.componentWillReceiveProps(this.props);
    }

    componentWillReceiveProps(nextProps: Props<TValue>) {
        if (this.props.value !== nextProps.value) {
            this.setState({ groupExpansionStates: new Map([...this.state.groupExpansionStates].filter(([_, state]) => state)) });
        }

        if (DEBUG) {
            this._validateChildren(nextProps);
        }
    }

    private _validateChildren(props: Props<TValue> = this.props, children: Children<TValue> = props.children, usedValues: Set<TValue> = new Set<TValue>(), usedGroups: Set<TValue> = new Set<TValue>()) {
        toArray(children || [])
            .flatten()
            .forEach((child) => {
                if (!child || isImpossibleChild(child) || isLabel(child)) { return; }

                if (isGroup<TValue>(child) || isOption<TValue>(child)) {
                    const
                        value = isGroup<TValue>(child) ? child.props.id : child.props.value,
                        set = !isGroup<TValue>(child) || props.groupSelectionEnabled ? usedValues : usedGroups;
                    if (set.has(value)) {
                        throw new Error(`Duplicate value in ${child.type.displayName} ${isGroup<TValue>(child) && props.groupSelectionEnabled ? 'as Option' : ''}: ${value}`);
                    }
                    set.add(value);


                    if (isGroup<TValue>(child) && child.props.children) {
                        this._validateChildren(props, child.props.children, usedValues, usedGroups);
                    }
                    return;
                }

                assertNever(child, 'Children of Listbox must be instances of Listbox.Label, Listbox.Group or Listbox.Option.');
            });
    }

    private _onFilterChange: ComponentEventHandler1<string> = (_sender, value) => {
        if ((this.props as any).onSearchChange) {
            (this.props as any).onSearchChange(_sender, value);
        }

        this.setState({ filter: value });
    };

    private _onOptionClick = (value: TValue) => {
        if (this.props.onChange && !this.props.disabled) {
            this.props.onChange(this, value);

            if (this.props.clearSearchFilterAutomatically) {
                this.setState({ filter: '' });
            }
        }
    };

    private _onToggleGroupClick: ReactEventHandler2<TValue, boolean> = (groupID, expanded) => {
        this.setState({ groupExpansionStates: new Map(this.state.groupExpansionStates).set(groupID, expanded) });
    };

    private _getInnerText(component: ReactNode): string {
        if (!component || !isValidElement(component!)) {
            return typeof component === 'string' ? component : '';
        }

        const { resource } = component.props as any;
        if (isResource(resource)) {
            return typeof resource === 'string' ? resource : this.context.intl.formatMessage(resource);
        }

        if (isGroup<TValue>(component)) {
            return isResource(component.props.title) ?
                typeof component.props.title === 'string' ? component.props.title : this.context.intl.formatMessage(component.props.title) :
                this._getInnerText(component.props.title);
        }

        if (isOption<TValue>(component) && !!component.props.keywords) {
            return component.props.keywords;
        }

        return toArray(component.props && (component.props as any).children || []).map(child => this._getInnerText(child)).join(' ');
    }

    private _isVisible(child: Child<TValue>): boolean {
        let childInnerText: string;
        return !this.props.searchable || !this.state.filter ||
                !!(childInnerText = this._getInnerText(child).toLowerCase()) &&
                this.state.filter.toLowerCase().split(' ').every(word => childInnerText.includes(word));
    }

    private _renderChildren(children: Children<TValue> = this.props.children, statsToFill: { isSubselected: boolean; } = { isSubselected: false }, parents: Child<TValue>[] = []): ReactNode[] {
        return toArray(children || [])
            .map((child, index) => keyify(child as any as Child<TValue>[], `${index}`))
            .flatten()
            .map(({ key, child }) => {
                if (!child || isImpossibleChild(child)) { return null; }

                if (isLabel(child)) {
                    return <div key={key} className={`label ${child.props.className}`}>{child.props.children}</div>;
                }

                if (isGroup<TValue>(child)) {
                    const
                        { value, appearance, searchable } = this.props,
                        { filter, groupExpansionStates } = this.state,
                        { id, title, disabled, className, children } = child.props,
                        selectable = this.props.groupSelectionEnabled && child.props.selectable,

                        stats = { isSubselected: false },
                        subchildren = this._renderChildren(children || null, stats, [...parents, child]),
                        expanded = !disabled && (
                            groupExpansionStates.get(id) === true ||
                            searchable && filter ||
                            stats.isSubselected && groupExpansionStates.get(id) !== false ||
                            !subchildren.length
                        ),
                        selected = selectable && id === value;

                    statsToFill.isSubselected = statsToFill.isSubselected || selected || stats.isSubselected;

                    return !searchable || !filter || this._isVisible(child) || subchildren.length ?
                        appearance === Appearance.LINES ?
                            <div key={`index-${key}`}
                                className={
                                    `group ${className
                                    }${selectable ? ' selectable' : ''
                                    }${selected ? ' selected' : ''
                                    }${disabled ? ' disabled' : ''}`
                                }>
                                <span
                                    className={`toggle fa ${expanded ? 'fa-minus-square-o' : 'fa-plus-square-o'} `}
                                    onClick={!disabled ? this._onToggleGroupClick.bind(this, id, !expanded) : undefined} />
                                <span
                                    className="title"
                                    onClick={
                                        !disabled ?
                                            !selectable ?
                                                this._onToggleGroupClick.bind(this, id, !expanded) :
                                                this._onOptionClick.bind(this, id) :
                                            undefined
                                    } >
                                    {isResource(title) ? <FormattedResource resource={title} /> : title}
                                </span>
                                {expanded ? <div className="content">{subchildren}</div> : null}
                            </div> :
                            <div key={`index-${key}`} className={`group ${subchildren.length ? '' : 'empty '}${className}`}>
                                <div className="connector" ><div className="horizontal" /><div className="dot" /><div className="vertical" /></div>
                                <div className="title">{isResource(title) ? <FormattedResource resource={title} /> : title}</div>
                                <div className="content">{subchildren}</div>
                            </div> :
                        null;
                }

                if (isOption<TValue>(child)) {
                    const
                        { value, disabled, className, children } = child.props,
                        selected = this.props.value === value;

                    statsToFill.isSubselected = statsToFill.isSubselected || selected;

                    // element is visible if it's parent maches the filter or if it maches the filter
                    return parents.some(parent => this._isVisible(parent)) || this._isVisible(child) ?
                        this.props.appearance === Appearance.LINES ?
                            <div key={key}
                                className={
                                    `option ${className
                                    }${selected ? ' selected' : ''
                                    }${disabled ? ' disabled' : ''}`
                                }
                                onClick={!disabled ? this._onOptionClick.bind(this, value) : undefined}>
                                {children}
                            </div> :
                            <div key={key} className="option">
                                <div className="connector" ><div className="horizontal" /><div className="dot" /><div className="vertical" /></div>
                                <Checkbox appearance={Checkbox.Appearance.SWITCH} disabled={disabled} value={selected} onChange={!disabled && !selected ? this._onOptionClick.bind(this, value) : undefined}>
                                    {children}
                                </Checkbox>
                            </div> :
                        null;
                }

                return assertNever(child);
            })
            .filter(v => v);
    }

    render() {
        return (
            <div className={`listbox ${this.props.appearance === Appearance.LINES ? 'lines ' : 'switches '}${this.props.className}`} >
                <div className="toolbar flex-row">
                    {this.props.searchable ? <div className="tool main-col"><TextInput value={this.state.filter} autofocus={(this.props as any).autofocusOnSearch} icon="search" placeholder={this.props.searchPlaceholder} onChange={this._onFilterChange} /></div> : null}
                    {this.props.tools ? toArray(this.props.tools).map((tool, index) => <div key={tool.key || index} className="tool">{tool}</div>) : null}
                </div>
                {/* TODO make this node focusable and controllable with up and down keys */}
                <div tabIndex={0} className="children" >
                    {this._renderChildren().or(this.state.filter ? <FormattedResource resource={{ id: 'search.nothing_found' }} /> : null)}
                </div>
            </div>
        );
    }
}
